import ClickOutside from 'react-click-outside';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import colors from 'shared-assets/lib/colors';
import { Button } from 'shared-assets/components/Elements';
import MenuAim from 'shared-assets/components/MenuAim';
import Spinner from 'shared-assets/components/Spinner';

// All of the current styling of this button is hardcoded
// to the "link" style. Once we need Menus with alternate
// styling's we will have to take into consideration where this
// component is used. As of right now, it's used in these places place:

// 1. dashboard-ui/components/Actions/index.js
// 2. dashboard-ui/components/AppointmentCard/index.js
// 3. dashboard-ui/components/AppointmentMenuStatus/index.js
// 4. appointment-ui/components/Footer.js
// 5. admin-ui

const Wrapper = styled.div`
  > button {
    cursor: pointer;

    display: flex;
    align-items: center;

    .isvg {
      margin-left: 0.5rem;
      display: flex;
      align-items: center;
    }

    svg {
      width: 0.6875rem;
      height: auto;
    }

    &.link svg path {
      fill: ${colors.primary};
    }
    &.default svg path {
      fill: #fff;
    }
    &.primary svg path {
      fill: #fff;
    }
    &.secondary svg path {
      fill: #fff;
    }
    &.success svg path {
      fill: #fff;
    }
    &.warning svg path {
      fill: #fff;
    }
    &.error svg path {
      fill: #fff;
    }
    &:disabled svg path {
      fill: ${colors.light};
    }
  }

  .dropDown {
    margin: 0;
    padding: 5px 0;
    list-style-type: none;
    position: absolute;
    background: #fff;
    border: 1px solid rgba(${colors.secondaryRGB.join(', ')}, 0.16);
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
    z-index: 20;
    min-width: 200px;
    max-width: 400px;
    background-clip: padding-box;
    border-radius: 0;
  }

  ul li button {
    color: ${colors.primary};
    background: none;
    border: none;
    width: 100%;
    padding: 0.5rem;
    text-align: left;
    outline: none;
    font-size: 1rem;
    cursor: pointer;

    &:hover,
    &.hover {
      background: ${colors.menuButton.hoverColor};
      color: ${colors.primary};
    }
    &:disabled {
      color: ${colors.secondary};
      background: none;
      cursor: not-allowed;
    }
  }

  ul li a {
    color: ${colors.primary};
    display: block;
    text-decoration: none;
    width: 100%;
    padding: 0.5rem;

    &:hover {
      background: ${colors.menuButton.hoverColor};
      color: ${colors.primary};
    }
  }
  & ul li hr {
    margin: 0.25rem auto;
  }

  ul li.submenu {
    position: relative;

    span.icon {
      float: right;

      svg {
        height: 0.8rem;

        path {
          fill: ${colors.primary};
        }
      }
    }
  }

  ${props => {
    if (props.direction === 'up' && props.button && props.listItems) {
      return `
        ul {
          display: flex;
          flex-direction: column-reverse;
        }

        .listItemWrapper {
          position: relative;
          top: -${
            props.button.getBoundingClientRect().height +
            props.listItems.getBoundingClientRect().height
          }px;

        }
      `;
    }
  }};

  ${props => {
    if (props.position === 'right' && props.button && props.listItems) {
      return `
        .listItemWrapper{
          position: relative;
          right: ${
            props.listItems.getBoundingClientRect().width -
            props.button.getBoundingClientRect().width
          }px;
        }
      `;
    }
  }};

  ${props => {
    if (props.direction === 'down' && props.button && props.listItems) {
      return `
        .directionInverted {
          position: relative;
          top: -${
            props.button.getBoundingClientRect().height +
            props.listItems.getBoundingClientRect().height
          }px;
        }
      `;
    }
  }};
`;

const SubMenu = styled.ul`
  && {
    position: relative;
    padding: 0;
    max-height: 300px;
    overflow: auto;
  }
`;

// Polyfill Element.closest (for IE/Edge)
// https://developer.mozilla.org/en-US/docs/Web/API/Element/closest#Polyfill
if (!Element.prototype.matches) {
  Element.prototype.matches =
    Element.prototype.msMatchesSelector ||
    Element.prototype.webkitMatchesSelector;
}

if (!Element.prototype.closest) {
  Element.prototype.closest = function (s) {
    var el = this;

    do {
      if (el.matches(s)) return el;
      el = el.parentElement || el.parentNode;
    } while (el !== null && el.nodeType === 1);
    return null;
  };
}

class Menu extends React.Component {
  static defaultProps = {
    children: '',
    className: '',
    direction: 'down',
    disabled: false,
    ensureDropdownOverlays: false,
    initiallyVisible: false,
    onClick: () => {},
    onClickOutside: toggleDropDownFunc => {
      toggleDropDownFunc();
    },
    position: 'left',
    printInProgress: false,
    onMenuOpen: () => {},
    render: undefined,
    options: undefined,
    'data-testid': undefined,
  };

  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.func,
    ]),
    className: PropTypes.string,
    direction: PropTypes.oneOf(['up', 'down']),
    disabled: PropTypes.bool,
    ensureDropdownOverlays: PropTypes.bool,
    initiallyVisible: PropTypes.bool,
    onClick: PropTypes.func,
    onClickOutside: PropTypes.func,
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.shape({ label: PropTypes.string, options: PropTypes.node }),
      ]),
    ),
    position: PropTypes.oneOf(['left', 'right']),
    printInProgress: PropTypes.bool,
    onMenuOpen: PropTypes.func,
    render: PropTypes.node,
    'data-testid': PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      isDropdownVisible: !!props.initiallyVisible,
    };
    this.ensureDropdownOverlays = this.ensureDropdownOverlays.bind(this);
    this.menuWrapper = React.createRef();

    this.button = null;

    this.setButtonRef = element => {
      this.button = element;
    };
  }

  componentWillUnmount() {
    window.cancelAnimationFrame(this.raf);
  }

  ensureDropdownOverlays() {
    // This will guarantee the dropdown is above other elements
    // (Useful in the AppointmentCard body so it overlays the footer)
    if (
      this.props.ensureDropdownOverlays &&
      this.state.isDropdownVisible &&
      this.listItems &&
      this.button
    ) {
      const buttonRect = this.button.getBoundingClientRect();
      const listItems = this.listItems.getBoundingClientRect();

      let right = buttonRect.x + buttonRect.width;
      let top = buttonRect.y + buttonRect.height;

      if (this.props.direction === 'up') {
        top = `${buttonRect.y + buttonRect.height - listItems.height}px`;
      }

      if (this.props.position === 'right') {
        right = `${buttonRect.x - listItems.width}px`;
      }

      this.listItems.style.display = 'block';
      this.listItems.style.position = 'fixed';
      this.listItems.style.right = `${right}px`;
      this.listItems.style.top = `${top}px`;
      this.raf = window.requestAnimationFrame(this.ensureDropdownOverlays);
    } else if (
      this.props.ensureDropdownOverlays &&
      this.state.isDropdownVisible &&
      this.listItems
    ) {
      this.listItems.style.display = 'none';
      this.raf = window.requestAnimationFrame(this.ensureDropdownOverlays);
    }
  }

  handleClickInDropdown = e => {
    e.stopPropagation();
    this.setState({
      isDropdownVisible: false,
    });
  };

  handleButtonClick = e => {
    this.props.onClick(e);
    this.toggleDropdown();
  };

  checkMenuDirection = () => {
    if (!!this.menuWrapper && !!this.menuWrapper.current) {
      let wrapper = this.menuWrapper.current;
      let remaindingSpace = this.containerScrollHeight - wrapper.offsetTop;
      if (
        remaindingSpace < this.listItems.clientHeight &&
        this.button &&
        wrapper.offsetTop >
          this.listItems.clientHeight + this.button.offsetHeight &&
        remaindingSpace < wrapper.offsetTop
      ) {
        this.listItems.style.display = 'block';
        wrapper.style.top = `-${
          this.listItems.clientHeight + this.button.offsetHeight
        }px`;
      }
    }
  };

  toggleDropdown = () => {
    let newDropdownVisibleState = !this.state.isDropdownVisible;
    this.containerScrollHeight =
      (this.menuRef &&
        this.menuRef.offsetParent &&
        this.menuRef.offsetParent.scrollHeight) ||
      window.innerHeight;
    this.setState(
      {
        isDropdownVisible: newDropdownVisibleState,
      },
      () => {
        // Prevent parent container from scrolling while the menu is open (UX requirement)
        if (this.button) {
          const scrollableContainers = this.button.closest(
            '.prevent-scroll-when-menu-is-open',
          );
          if (this.state.isDropdownVisible && scrollableContainers) {
            scrollableContainers.style.overflowY = 'hidden';
          } else if (!this.state.isDropdownVisible && scrollableContainers) {
            scrollableContainers.style.overflowY = 'auto';
          }
        }

        this.raf = window.requestAnimationFrame(this.ensureDropdownOverlays);

        // Force update so our styled-components get a chance to
        // recompute their dynamic js
        this.forceUpdate();
      },
    );

    if (newDropdownVisibleState) {
      this.props.onMenuOpen();
      setTimeout(() => {
        this.checkMenuDirection.apply(this);
      }, 1);
    }
  };

  getMenuContainer = () => {
    return (
      this.menuWrapper &&
      this.menuWrapper.current &&
      this.menuWrapper.current.offsetParent
    );
  };

  render() {
    let { children, onClickOutside, onMenuOpen, ...rest } = this.props;
    let className = this.props.className;
    className += ` ${this.state.isDropdownVisible ? 'active' : ''}`;
    let renderProp = !!children && typeof children === 'function';
    let currentMenuInstance = this;
    let ComposedChildren = children;

    if (!!renderProp) {
      // eslint-disable-next-line react/display-name
      ComposedChildren = React.forwardRef(children);
      ComposedChildren.displayName = 'MenuComposedChild';
    }

    return (
      <Wrapper
        data-testid={this.props['data-testid']}
        button={this.button}
        direction={this.props.direction}
        listItems={this.listItems}
        position={this.props.position}
        ref={element => (this.menuRef = element)}
      >
        <If condition={!!renderProp}>
          <ComposedChildren
            {...rest}
            className={className}
            onClick={currentMenuInstance.handleButtonClick}
            ref={this.setButtonRef}
          />
        </If>
        <If condition={!renderProp}>
          <Button
            type="button"
            ref={this.setButtonRef}
            onClick={this.handleButtonClick}
            className={className}
            style={{
              display: 'flex',
              justifyContent: 'center',
              lineHeight: '1.25rem',
              paddingTop: '6px',
            }}
            disabled={this.props.disabled}
            data-testid="itemMenu"
          >
            <If condition={this.props.printInProgress}>
              <Spinner size="1rem" />
              &nbsp;
            </If>
            {children}
            <SVG
              cacheGetRequests
              src={`/static/assets/icons/chevron-${this.props.direction}.svg`}
            />
          </Button>
        </If>
        <If condition={this.state.isDropdownVisible}>
          <div ref={this.menuWrapper} className="listItemWrapper">
            <ClickOutside
              onClickOutside={e =>
                this.props.onClickOutside(this.toggleDropdown, e)
              }
            >
              <If condition={this.props.options}>
                <ul
                  data-testid="menu-dropdown"
                  className="dropDown"
                  ref={c => {
                    this.listItems = c;
                  }}
                  onClick={this.handleClickInDropdown}
                  role="presentation"
                >
                  <For
                    each="component"
                    of={
                      this.props.direction === 'up'
                        ? this.props.options.slice().reverse()
                        : this.props.options
                    }
                    index="index"
                  >
                    <Choose>
                      <When
                        condition={
                          component && component.hasOwnProperty('options')
                        }
                      >
                        <MenuAim
                          position={this.props.position}
                          direction={this.props.direction}
                          key={index}
                          menu={
                            <div data-testid="submenu">
                              <SubMenu className="dropDown">
                                <For
                                  each="subComponent"
                                  of={component.options}
                                  index="subIndex"
                                >
                                  <li key={`sub_${subIndex}`}>
                                    {subComponent}
                                  </li>
                                </For>
                              </SubMenu>
                            </div>
                          }
                          mainMenu={this.menuWrapper}
                          getContainer={this.getMenuContainer}
                        >
                          {isOpen => (
                            <li className="submenu" key={index}>
                              <button
                                onClick={e => e.stopPropagation()}
                                className={isOpen ? 'hover' : ''}
                              >
                                {component.label}
                                <SVG
                                  className="icon"
                                  cacheGetRequests
                                  src={require('shared-assets/assets/icons/angle-bracket-right.svg')}
                                />
                              </button>
                            </li>
                          )}
                        </MenuAim>
                      </When>
                      <Otherwise>
                        <li key={index}>{component}</li>
                      </Otherwise>
                    </Choose>
                  </For>
                </ul>
              </If>
              <If condition={this.props.render}>
                <div className="dropDown">{this.props.render}</div>
              </If>
            </ClickOutside>
          </div>
        </If>
      </Wrapper>
    );
  }
}

export default Menu;
